import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowBackRounded,
  CheckCircleRounded,
  RadioButtonUncheckedRounded,
  MenuBookRounded,
} from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import { getMyHomework, setHomeworkDone } from '../services/api';
import { StudentHomeworkItem } from '../types';

const fmtDay = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const subjectLabel = (s?: string | null): string => {
  if (!s) return 'General';
  return s.charAt(0).toUpperCase() + s.slice(1);
};

/** Group homework by the week it was assigned (server returns newest first). */
const groupByWeek = (list: StudentHomeworkItem[]) => {
  const groups: { week: string; items: StudentHomeworkItem[] }[] = [];
  for (const item of list) {
    const last = groups[groups.length - 1];
    if (last && last.week === item.week_start) last.items.push(item);
    else groups.push({ week: item.week_start, items: [item] });
  }
  return groups;
};

const MyHomework: React.FC = () => {
  const navigate = useNavigate();
  const [list, setList] = useState<StudentHomeworkItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const loadList = async () => {
    setLoading(true);
    setError(null);
    try {
      setList(await getMyHomework());
    } catch (e: any) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadList();
  }, []);

  const toggle = async (item: StudentHomeworkItem) => {
    setBusyId(item.assignment_id);
    setError(null);
    try {
      const res = await setHomeworkDone(item.assignment_id, !item.completed);
      setList((prev) =>
        prev.map((h) =>
          h.assignment_id === item.assignment_id ? { ...h, completed: res.completed } : h
        )
      );
    } catch (e: any) {
      setError(e.message || 'Failed to update homework');
    } finally {
      setBusyId(null);
    }
  };

  const doneCount = useMemo(() => list.filter((h) => h.completed).length, [list]);
  const groups = useMemo(() => groupByWeek(list), [list]);

  return (
    <div className="mx-auto w-full max-w-2xl px-4 py-6">
      <button
        onClick={() => navigate('/chat')}
        className="mb-4 flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
      >
        <ArrowBackRounded fontSize="small" /> Back to chat
      </button>
      <h1 className="text-xl font-bold text-slate-900">My Homework</h1>
      <p className="mb-5 text-sm text-slate-500">
        {list.length > 0
          ? `${doneCount} of ${list.length} done — tap a card when you finish it.`
          : 'Homework your teacher sets will show up here.'}
      </p>

      {loading ? (
        <p className="py-10 text-center text-slate-500">Loading…</p>
      ) : error ? (
        <p className="py-10 text-center text-rose-600">{error}</p>
      ) : list.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 p-8 text-center text-slate-500">
          <MenuBookRounded className="mb-2 text-slate-300" />
          <p className="text-sm">No homework right now — you're all caught up! 🎉</p>
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          {groups.map((group) => (
            <div key={group.week}>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-violet-600">
                Week of {fmtDay(group.week)}
              </p>
              <div className="flex flex-col gap-3">
                {group.items.map((h) => (
                  <button
                    key={h.assignment_id}
                    onClick={() => toggle(h)}
                    disabled={busyId === h.assignment_id}
                    className={`flex items-center justify-between gap-3 rounded-xl border p-4 text-left transition disabled:opacity-60 ${
                      h.completed
                        ? 'border-emerald-200 bg-emerald-50/60'
                        : 'border-[#E9E6F4] bg-white hover:border-violet-300'
                    }`}
                  >
                    <div className="min-w-0">
                      <div className="mb-1 flex items-center gap-2">
                        <span className="rounded-md bg-violet-100 px-2 py-0.5 text-[11px] font-semibold text-violet-700">
                          {subjectLabel(h.subject)}
                        </span>
                        {h.due_date && (
                          <span className="text-[11px] text-slate-400">Due {fmtDay(h.due_date)}</span>
                        )}
                      </div>
                      <p
                        className={`text-sm font-medium ${
                          h.completed ? 'text-slate-500 line-through' : 'text-slate-800'
                        }`}
                      >
                        {h.task}
                      </p>
                    </div>
                    <span className="flex-none">
                      {busyId === h.assignment_id ? (
                        <CircularProgress size={20} sx={{ color: '#7c3aed' }} />
                      ) : h.completed ? (
                        <CheckCircleRounded className="text-emerald-500" />
                      ) : (
                        <RadioButtonUncheckedRounded className="text-slate-300" />
                      )}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyHomework;
